import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faBars } from '@fortawesome/free-solid-svg-icons';
import Logo from './Logo';
import { useLanguage } from '../i18n/LanguageContext';
import { translations } from '../i18n/translations';

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  z-index: 900;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 0 2.5rem;
  height: 82px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);

  @media (max-width: 1200px) {
    height: auto;
    padding: 10px 1.5rem;
  }
    @media (max-width: 1100px) {
      padding: 0 1.1rem;
    }

  @media (max-width: 640px) {
    padding: 0 1.1rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.85rem;
    height: 70px;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: center;
  min-width: 0;

  @media (max-width: 1200px) {
    gap: 6px;
    row-gap: 8px;
    flex-wrap: wrap;
  }
    @media (max-width: 1100px) {
      justify-content: flex-start;
    }
  
  @media (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 76px;
    left: 1rem;
    right: 1rem;
    padding: 18px;
    background: rgba(255, 255, 255, 0.97);
    border-radius: var(--radius-lg);
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: var(--shadow-card);
    gap: 6px;
    align-items: stretch;
  }

  @media (max-width: 480px) {
    top: 68px;
    padding: 14px;
  }
`;

const NavItem = styled.div`
  padding: 0 12px;
  position: relative;

  @media (max-width: 1200px) {
    padding: 0 8px;
  }

  @media (max-width: 768px) {
    width: 100%;
    text-align: left;
    padding: 10px 0;
  }

  @media (max-width: 480px) {
    padding: 8px 0;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: var(--text-main);
  font-weight: 600;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.02em;
  transition: color 0.2s ease, transform 0.2s ease;
  line-height: 1.4;
  white-space: nowrap;
  
  &:after {
    content: ${props => props.$hasDropdown ? '"▼"' : '""'};
    font-size: 9px;
    display: inline-block;
    transform: ${props => {
      if (!props.$hasDropdown) return 'translateY(-1px)';
      return props.$isOpen ? 'rotate(180deg) translateY(1px)' : 'translateY(-1px)';
    }};
    transition: transform 0.2s ease;
  }
  
  &:hover {
    color: var(--primary);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    white-space: normal;
  }

  @media (max-width: 1200px) {
    font-size: 14px;
  }
`;

const DropdownMenu = styled.ul`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 230px;
  padding: 18px 12px 12px;
  background-color: rgba(255, 255, 255, 0.98);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-soft);
  border: 1px solid rgba(226, 232, 240, 0.8);
  z-index: 10;
  list-style-type: none;
  margin: 0;

  &:before {
    content: '';
    position: absolute;
    top: -10px;
    left: 24px;
    width: 16px;
    height: 16px;
    background: inherit;
    border-left: inherit;
    border-top: inherit;
    transform: rotate(45deg);
    border-radius: 3px;
    z-index: 0;
  }

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    border: none;
    box-shadow: none;
    padding: 0;
    margin-top: 10px;
    &:before {
      display: none;
    }
    background: transparent;
    display: ${({ $isOpen }) => ($isOpen ? 'grid' : 'none')};
    gap: 6px;
  }
`;

const DropdownItem = styled.li`
  padding: 10px 12px;
  border-radius: var(--radius-sm);

  a {
    text-decoration: none;
    color: var(--text-muted);
    font-weight: 500;
    font-size: 14px;
    display: block;
    transition: color 0.2s ease;
  }

  &:hover {
  background: rgba(12, 77, 162, 0.08);

    a {
      color: var(--primary);
    }
  }

  @media (max-width: 768px) {
    padding: 10px 6px;

    a {
      font-size: 13.5px;
      padding: 2px 0;
    }
  }
`;

const ButtonLink = styled(Link)`
  background: linear-gradient(135deg, var(--primary) 0%, #1c6be1 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 999px;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  box-shadow: var(--shadow-soft);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-hover);
  }

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    justify-content: center;
  }

  @media (max-width: 1200px) {
    padding: 10px 18px;
    font-size: 14px;
  }
    @media (max-width: 1100px) {
      padding: 9px 14px;
      font-size: 13.5px;
      letter-spacing: 0.01em;
    }
`;

const IconContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 0 0 auto;

  @media (max-width: 768px) {
    display: none;
  }
    @media (max-width: 1100px) {
      gap: 8px;
    }
`;

const LanguageSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 10px;
  border-left: 1px solid rgba(226, 232, 240, 0.8);

  @media (max-width: 1200px) {
    gap: 6px;
    padding-left: 8px;
  }
    @media (max-width: 1100px) {
      gap: 5px;
      padding-left: 6px;
    }

  @media (max-width: 768px) {
    border-left: none;
    padding-left: 0;
  }
`;

const MobileLanguageRow = styled.div`
  display: none;
  padding: 8px 0;

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    width: 100%;
  }
`;

const LanguageButton = styled.button`
  border: 1px solid ${({ $active }) => ($active ? 'rgba(12, 77, 162, 0.45)' : 'rgba(226, 232, 240, 0.9)')};
  background: ${({ $active }) => ($active ? 'rgba(12, 77, 162, 0.1)' : 'transparent')};
  color: var(--text-main);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(12, 77, 162, 0.45);
  }

  @media (max-width: 768px) {
    width: auto;
    justify-content: center;
  }
`;

const FlagImage = styled.img`
  width: 22px;
  height: 16px;
  border-radius: 3px;
  display: block;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.12);
`;

const Icon = styled.div`
  cursor: pointer;
  padding: 10px;
  color: var(--text-muted);
  border-radius: 50%;
  transition: background 0.2s ease, color 0.2s ease;
  
  &:hover {
    color: var(--primary);
  background: rgba(12, 77, 162, 0.08);
  }
`;

const MobileMenuButton = styled.div`
  display: none;
  cursor: pointer;
  font-size: 26px;
  color: var(--text-main);
  padding: 10px;
  border-radius: 12px;
  transition: background 0.2s ease, color 0.2s ease;
  
  @media (max-width: 768px) {
    display: block;
  }

  &:hover {
    background: rgba(15, 23, 42, 0.06);
    color: var(--primary);
  }
`;

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);
  const languageOptions = Object.keys(translations).map((code) => ({
    code,
    ...translations[code].meta
  }));

  useEffect(() => {
    const handleClickOutside = event => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuToggle = () => {
    setMenuOpen(prev => {
      const next = !prev;
      if (!next) {
        setOpenDropdown(null);
      }
      return next;
    });
  };

  const handleNavigate = () => {
    setMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (event, key) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setOpenDropdown(prev => (prev === key ? null : key));
  };

  const handleDropdownKeyDown = (event, key) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpenDropdown(prev => (prev === key ? null : key));
    }

    if (event.key === 'Escape') {
      setOpenDropdown(null);
      event.currentTarget.blur();
    }
  };
  
  return (
    <HeaderContainer>
      <Logo />
      
      <MobileMenuButton onClick={handleMenuToggle}>
        <FontAwesomeIcon icon={faBars} />
      </MobileMenuButton>
      
      <Nav $isOpen={menuOpen} ref={navRef}>
        <NavItem>
          <StyledLink
            to="/tracking"
            $hasDropdown
            $isOpen={openDropdown === 'tracking'}
            aria-haspopup="true"
            aria-expanded={openDropdown === 'tracking'}
            onClick={event => toggleDropdown(event, 'tracking')}
            onKeyDown={event => handleDropdownKeyDown(event, 'tracking')}
          >
            {t('header.nav.shipTrackQuote')}
          </StyledLink>
          <DropdownMenu $isOpen={openDropdown === 'tracking'}>
            <DropdownItem><Link to="/quote" onClick={handleNavigate}>{t('header.nav.getQuote')}</Link></DropdownItem>
          </DropdownMenu>
        </NavItem>
        
        <NavItem>
          <StyledLink
            to="/services"
            $hasDropdown
            $isOpen={openDropdown === 'services'}
            aria-haspopup="true"
            aria-expanded={openDropdown === 'services'}
            onClick={event => toggleDropdown(event, 'services')}
            onKeyDown={event => handleDropdownKeyDown(event, 'services')}
          >
            {t('header.nav.services')}
          </StyledLink>
          <DropdownMenu $isOpen={openDropdown === 'services'}>
            <DropdownItem><Link to="/services/conseils" onClick={handleNavigate}>{t('header.nav.consulting')}</Link></DropdownItem>
            <DropdownItem><Link to="/services/routiers" onClick={handleNavigate}>{t('header.nav.road')}</Link></DropdownItem>
          </DropdownMenu>
        </NavItem>
        
        <NavItem>
          <StyledLink to="/careers" onClick={handleNavigate}>{t('header.nav.careers')}</StyledLink>
        </NavItem>
        
        <NavItem>
          <StyledLink
            to="/about"
            $hasDropdown
            $isOpen={openDropdown === 'company'}
            aria-haspopup="true"
            aria-expanded={openDropdown === 'company'}
            onClick={event => toggleDropdown(event, 'company')}
            onKeyDown={event => handleDropdownKeyDown(event, 'company')}
          >
            {t('header.nav.company')}
          </StyledLink>
          <DropdownMenu $isOpen={openDropdown === 'company'}>
            <DropdownItem><Link to="/about" onClick={handleNavigate}>{t('header.nav.about')}</Link></DropdownItem>
            <DropdownItem><Link to="/contact" onClick={handleNavigate}>{t('header.nav.contact')}</Link></DropdownItem>
          </DropdownMenu>
        </NavItem>
        
        <NavItem>
          <ButtonLink to="/quote" onClick={handleNavigate}>{t('header.nav.cta')}</ButtonLink>
        </NavItem>
        <MobileLanguageRow>
          <LanguageSwitcher aria-label={t('header.languageLabel')}>
            {languageOptions.map((option) => (
              <LanguageButton
                key={option.code}
                type="button"
                $active={language === option.code}
                onClick={() => setLanguage(option.code)}
                aria-pressed={language === option.code}
                aria-label={option.label}
                title={option.label}
              >
                <FlagImage
                  src={option.flagImage || ''}
                  alt={option.label}
                  loading="lazy"
                />
              </LanguageButton>
            ))}
          </LanguageSwitcher>
        </MobileLanguageRow>
      </Nav>
      
      <IconContainer>
        <LanguageSwitcher aria-label={t('header.languageLabel')}>
          {languageOptions.map((option) => (
            <LanguageButton
              key={option.code}
              type="button"
              $active={language === option.code}
              onClick={() => setLanguage(option.code)}
              aria-pressed={language === option.code}
              aria-label={option.label}
              title={option.label}
            >
              <FlagImage
                src={option.flagImage || ''}
                alt={option.label}
                loading="lazy"
              />
            </LanguageButton>
          ))}
        </LanguageSwitcher>
        <Icon aria-label={t('header.searchLabel')}>
          <FontAwesomeIcon icon={faSearch} size="lg" />
        </Icon>
      </IconContainer>
    </HeaderContainer>
  );
};

export default Header;